// Multiple Charts Context Implementation
// Architecture: v4.0.0 Multiple Charts System (Clean Implementation)
// Replaces: AstrologyChartContext.tsx (single chart architecture)

import { devLog, devWarn, devError } from '../../../utils/devLog';
import { getPlaceName } from '../../../utils/apiDataTransformers';
import { getLocalStorageItem, setLocalStorageItem } from '../../../utils/localStorageUtils';
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { getDefaultMuhurtaChakraVisibility } from '../../../config/muhurtaDefaults';
import { getDefaultAshtamshaChakraVisibility } from '../../../config/ashtamshaDefaults';
import { getDefaultNakshatraChakraVisibility } from '../../../config/nakshatraDefaults';
import {
  MultipleChartsContextType,
  ChartInstance,
  WorkspaceState,
  ChartLoadingOptions,
  ChartMarker,
  ChartStateSnapshot,
  SnapshotSource,
  MAX_CHART_HISTORY,
  CURRENT_LIVE_INDEX,
  getEffectiveHistoryIndex
} from '../../../types/MultipleCharts';
import { ParsedChartData } from '../../../models/types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { generateChartId } from '../../../utils/chartIdGenerator';
import { getStorageService, StorageService } from '../../../services/StorageService';
import { globalEventStream, createEvent, emitChartActivated, emitChartDeactivated } from './universal_architecture/core/EventStream';
import { BaseEvent } from './universal_architecture/types/EventTypes';
import { planetDragIntegrationService } from '../../../services/PlanetDragIntegrationService';
// StateViewportManager now handles state caching in TabChartEventManager

// Pipeline event emitter for coordinated loading stages
const emitPipelineEvent = (type: string, chartId: string, extra?: Record<string, any>) => {
  globalEventStream.emit(createEvent(type, 'MultipleChartsContext', { chartId, ...extra }));
};

// DRY helper: Generate unique snapshot ID
const generateSnapshotId = (): string =>
  `snapshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// DRY helper: Add snapshot to history, maintain MAX_CHART_HISTORY limit
const addSnapshotToHistory = (
  history: ChartStateSnapshot[],
  snapshot: ChartStateSnapshot
): ChartStateSnapshot[] => {
  const newHistory = [...history, snapshot];
  return newHistory.length > MAX_CHART_HISTORY
    ? newHistory.slice(-MAX_CHART_HISTORY)
    : newHistory;
};

/**
 * Multiple Charts Context
 * Central state management for multiple chart instances
 */
const MultipleChartsContext = createContext<MultipleChartsContextType | null>(null);

/**
 * Default workspace configuration
 */
const createDefaultWorkspace = (): WorkspaceState => ({
  id: 'default',
  name: 'Default Workspace',
  charts: [],
  activeChartId: null,
  chartLayout: 'single',
  created: new Date(),
  modified: new Date(),
  accessed: new Date(),
  isDefault: true,
  tabOrder: [],
  tabMeta: {},  // Lazy loading: metadata for tabs (name, lastAccessed)
  maxOpenCharts: 5
});

/**
 * Default chart instance configuration
 */
const createDefaultChartInstance = (
  chartId: string,
  chartData: ParsedChartData,
  metadata: any,
  apiResponse: any
): ChartInstance => {

  // Try to load saved settings from localStorage
  let savedSettings = {
    enableAnimations: true,
    showMuhurtaChakra: getDefaultMuhurtaChakraVisibility(),      // Centralized default
    showAshtamshaChakra: getDefaultAshtamshaChakraVisibility(),  // Centralized default D8
    showNakshatraChakra: getDefaultNakshatraChakraVisibility(),   // Centralized default Nakshatra

    // Universal divisional chakras defaults
    showD7Chakra: false,
    d7StartPoint: 'sunrise',
    showD8Chakra: false,
    d8StartPoint: 'sunrise',
    showD12Chakra: false,
    d12StartPoint: 'sunrise',
    showD20Chakra: false,
    d20StartPoint: 'sunrise',
    showD24Chakra: false,
    d24StartPoint: 'sunrise',
    showD30Chakra: false,
    d30StartPoint: 'sunrise',
    showD40Chakra: false,
    d40StartPoint: 'sunrise',
    showD60Chakra: false,
    d60StartPoint: 'sunrise',

    // Planet drag movement step defaults (draggable planets only)
    movementStepDegrees: { Lg: 1, Sy: 1, Ch: 1 }
  };

  const persisted = getLocalStorageItem<typeof savedSettings | null>(`chart-settings-${chartId}`, null);
  if (persisted) {
    savedSettings = { ...savedSettings, ...persisted };

    // MIGRATION: Legacy showAshtamshaChakra → showD8Chakra
    if (savedSettings.showAshtamshaChakra === true && !savedSettings.showD8Chakra) {
      savedSettings.showD8Chakra = true;
    }
  }

  // Extract chart datetime for original snapshot (use datetime_iso for precision)
  const basicInfo = apiResponse?.basic_info;
  const chartDateTime = basicInfo?.datetime_iso
    || (basicInfo?.date && basicInfo?.time ? `${basicInfo.date} ${basicInfo.time}` : new Date().toISOString());

  // Create original snapshot
  const originalSnapshot: ChartStateSnapshot = {
    id: `snapshot-original-${chartId}`,
    apiResponse,
    chartData,
    createdAt: new Date(),
    chartDateTime,
    source: 'original'
  };

  return {
    id: chartId,
    metadata,
    chartData,
    apiResponse,
    markers: [],
    activePresetId: null,
    zoomLevel: 1.0,
    displayMode: 'short',
    panPosition: { x: 0, y: 0 },
    settings: savedSettings,
    isLoading: false,
    lastAccessed: new Date(),
    accordionStates: {
      'planetary-positions': false,
      'panchanga': false,
      'chart-information': false,
      'astronomical': false
    },
    // History management - initialize with original snapshot
    history: [originalSnapshot],
    currentHistoryIndex: CURRENT_LIVE_INDEX  // current/live state (which is the original)
  };
};

// DRY helper: Restore history to chart instance (loadChart decomposition)
const restoreChartHistory = (
  chartInstance: ChartInstance,
  savedHistory: ChartStateSnapshot[] | undefined,
  savedHistoryIndex: number | undefined
): void => {
  if (!savedHistory || savedHistory.length === 0) return;

  chartInstance.history = savedHistory;
  chartInstance.currentHistoryIndex = savedHistoryIndex ?? CURRENT_LIVE_INDEX;

  // Update chartData and apiResponse from active snapshot
  const activeIndex = getEffectiveHistoryIndex(chartInstance.currentHistoryIndex, savedHistory.length);
  const activeSnapshot = savedHistory[activeIndex];

  if (activeSnapshot) {
    chartInstance.chartData = activeSnapshot.chartData;
    chartInstance.apiResponse = activeSnapshot.apiResponse;
  }

  devLog(`[MultipleChartsContext] 📂 Restored ${savedHistory.length} history snapshots, index: ${chartInstance.currentHistoryIndex}, active: ${activeSnapshot?.id}`);
};

// DRY helper: Build workspace update for chart load (loadChart decomposition)
const buildWorkspaceUpdate = (
  workspace: WorkspaceState,
  chartId: string,
  chartName: string,
  newLoadedIds: string[],
  newActiveId: string | null
): WorkspaceState => ({
  ...workspace,
  charts: newLoadedIds,
  tabOrder: newLoadedIds,
  activeChartId: newActiveId,
  tabMeta: {
    ...workspace.tabMeta,
    [chartId]: {
      id: chartId,
      name: chartName,
      lastAccessed: new Date()
    }
  },
  modified: new Date(),
  accessed: new Date()
});

// DRY helper: Get active snapshot info for event emission (loadChart decomposition)
const getActiveSnapshotInfo = (
  chartInstance: ChartInstance,
  chartId: string,
  fallbackApiResponse: any
): { snapshotId: string; apiResponse: any } => {
  const activeIndex = getEffectiveHistoryIndex(chartInstance.currentHistoryIndex, chartInstance.history.length);
  const activeSnapshot = chartInstance.history[activeIndex];

  return {
    snapshotId: activeSnapshot?.id || `snapshot-original-${chartId}`,
    apiResponse: activeSnapshot?.apiResponse || fallbackApiResponse
  };
};

interface MultipleChartsProviderProps {
  children: ReactNode;
  storageService?: StorageService;
}

/**
 * Multiple Charts Provider Component
 * Manages state and operations for multiple chart instances
 */
export const MultipleChartsProvider: React.FC<MultipleChartsProviderProps> = ({ 
  children,
  storageService = getStorageService()
}) => {
  // Debug mode disabled for production
  const debugMode = false;

  // Core state
  const [charts, setCharts] = useState<Map<string, ChartInstance>>(new Map());
  const [activeChartId, setActiveChartId] = useState<string | null>(null);
  const [loadedChartIds, setLoadedChartIds] = useState<string[]>([]);
  const [workspace, setWorkspace] = useState<WorkspaceState>(createDefaultWorkspace);
  
  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingCharts, setLoadingCharts] = useState<Set<string>>(new Set());

  // Flag to prevent saving workspace during restoration
  const isRestoringWorkspaceRef = React.useRef<boolean>(false);

  /**
   * Load chart from data directly (for share mode)
   */
  const loadChartFromData = useCallback(async (
    chartId: string,
    chartData: ParsedChartData,
    metadata: any,
    options: ChartLoadingOptions = {}
  ): Promise<void> => {
    try {
      // Check if already loaded
      if (charts.has(chartId)) {
        if (options.makeActive) {
          switchToChart(chartId);
        }
        return;
      }

      // Check workspace capacity
      if (loadedChartIds.length >= workspace.maxOpenCharts) {
        throw new Error(`Maximum ${workspace.maxOpenCharts} charts allowed in workspace`);
      }


      // Create chart instance from provided data
      const chartInstance = createDefaultChartInstance(
        chartId,
        chartData,
        metadata,
        chartData.apiResponse || {}
      );

      // Atomic state updates
      setCharts(prev => new Map([...prev, [chartId, chartInstance]]));

      // Save initial history (original snapshot) to storage
      if (chartInstance.history.length > 0) {
        storageService.updateChartHistory(chartId, chartInstance.history).catch(error => {
          devError(`[MultipleChartsContext] Failed to save initial history for ${chartId}:`, error);
        });
      }

      setLoadedChartIds(prev => {
        const insertIndex = options.insertAtIndex ?? prev.length;
        const newLoadedIds = [...prev];

        if (!newLoadedIds.includes(chartId)) {
          newLoadedIds.splice(insertIndex, 0, chartId);
        }

        return newLoadedIds;
      });

      // Update workspace with tabMeta for tab display
      const chartName = metadata?.name || 'Shared Chart';
      setWorkspace(prev => ({
        ...prev,
        // Sync activeChartId into workspace when makeActive is requested
        ...(options.makeActive ? { activeChartId: chartId } : {}),
        tabMeta: {
          ...prev.tabMeta,
          [chartId]: {
            id: chartId,
            name: chartName,
            lastAccessed: new Date()
          }
        },
        modified: new Date(),
        accessed: new Date()
      }));

      // Set as active if requested (React 18 batches this with previous updates)
      if (options.makeActive) {
        setActiveChartId(chartId);

        try {
          emitChartActivated(chartId, undefined, 'programmatic', 'MultipleChartsContext:loadChartFromData');
        } catch (error) {
          devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart activation:`, error);
        }
      }

      // Emit chart.loaded event (no timeout - state already updated)
      try {
        globalEventStream.emit(createEvent<BaseEvent>(
          'chart.loaded',
          'MultipleChartsContext',
          {
            chartId,
            timestamp: Date.now(),
            metadata: {
              totalChartsLoaded: loadedChartIds.length + 1,
              isActive: !!options.makeActive,
              chartName: metadata?.name || 'Shared Chart'
            }
          }
        ));
      } catch (error) {
        devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart.loaded event:`, error);
      }

      // Emit chart.data.updated event with snapshotId for initial render (no timeout - events are sync)
      try {
        const snapshotId = `snapshot-original-${chartId}`;
        globalEventStream.emit(createEvent(
          `chart.data.updated.${chartId}`,
          'MultipleChartsContext:loadChartFromData',
          {
            chartId,
            snapshotId,
            apiResponse: chartData.apiResponse
          }
        ));
      } catch (error) {
        devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart.data.updated event:`, error);
      }


    } catch (error) {
      devError('Failed to load chart from data:', chartId, error);
      throw error;
    }
  }, [charts, loadedChartIds, workspace]);

  /**
   * Load single chart into workspace
   * @param preloadedData - Optional pre-loaded SavedChart to skip storage call (DRY optimization)
   */
  const loadChart = useCallback(async (
    chartId: string,
    options: ChartLoadingOptions = {},
    preloadedData?: { chartData: ParsedChartData; metadata: any; history?: any[]; currentHistoryIndex?: number }
  ): Promise<void> => {
    try {
      // Check if already loaded
      if (charts.has(chartId)) {
        if (options.makeActive) {
          switchToChart(chartId);
        }
        return;
      }

      // Check workspace capacity
      if (loadedChartIds.length >= workspace.maxOpenCharts) {
        throw new Error(`Maximum ${workspace.maxOpenCharts} charts allowed in workspace`);
      }

      if (debugMode) devLog(`🔄 [MultipleChartsContext] Loading chart ${chartId}...`);

      // Pipeline: Stage 1 - LOADING
      emitPipelineEvent('chart.pipeline.started', chartId, { stage: 'loading' });

      // Set loading state
      setLoadingCharts(prev => new Set([...prev, chartId]));

      // Use preloaded data or load from storage
      let chartData: ParsedChartData;
      let metadata: any;
      let savedHistory: any[] | undefined;
      let savedHistoryIndex: number | undefined;

      if (preloadedData) {
        // Use pre-loaded data (skips redundant storage call)
        chartData = preloadedData.chartData;
        metadata = preloadedData.metadata;
        savedHistory = preloadedData.history;
        savedHistoryIndex = preloadedData.currentHistoryIndex;
        emitPipelineEvent('chart.pipeline.data_ready', chartId);
      } else {
        // Load chart data from storage
        const result = await storageService.loadChart(chartId);

        if (!result.success || !result.data) {
          emitPipelineEvent('chart.pipeline.error', chartId, { error: result.error || 'Unknown error' });
          throw new Error(`Failed to load chart: ${result.error || 'Unknown error'}`);
        }

        emitPipelineEvent('chart.pipeline.data_ready', chartId);
        chartData = result.data.chartData;
        metadata = result.data.metadata;
        savedHistory = result.data.history;
        savedHistoryIndex = result.data.currentHistoryIndex;
      }

      // Create chart instance with saved API data
      const chartInstance = createDefaultChartInstance(
        chartId,
        chartData,
        metadata,
        chartData.apiResponse || {} // Use saved API response from chartData
      );

      // Restore saved history if available (DRY helper)
      restoreChartHistory(chartInstance, savedHistory, savedHistoryIndex);

      // Pipeline: Stage 2 - UPDATING (single batched state update)
      emitPipelineEvent('chart.pipeline.stage', chartId, { stage: 'updating' });

      // Atomic state updates with proper race condition handling
      setCharts(prev => new Map([...prev, [chartId, chartInstance]]));

      // Pipeline: Context updated
      emitPipelineEvent('chart.pipeline.context_updated', chartId);

      // Calculate new loadedChartIds for immediate workspace save
      const insertIndex = options.insertAtIndex ?? loadedChartIds.length;
      const newLoadedIds = [...loadedChartIds];
      if (!newLoadedIds.includes(chartId)) {
        newLoadedIds.splice(insertIndex, 0, chartId);
      }

      // Use functional update to handle concurrent loading properly
      setLoadedChartIds(prev => {
        const idx = options.insertAtIndex ?? prev.length;
        const ids = [...prev];

        // Only add if not already present (avoid duplicates in concurrent loading)
        if (!ids.includes(chartId)) {
          ids.splice(idx, 0, chartId);
        }

        return ids;
      });

      // Update workspace with immediate state and save (DRY helper)
      const newActiveId = options.makeActive ? chartId : activeChartId;
      const chartName = metadata?.name || 'Unnamed Chart';
      const updatedWorkspace = buildWorkspaceUpdate(workspace, chartId, chartName, newLoadedIds, newActiveId);

      // Skip workspace update during restoration to prevent overwriting correct state
      if (!isRestoringWorkspaceRef.current) {
        setWorkspace(updatedWorkspace);
        storageService.saveDefaultWorkspace(updatedWorkspace).catch(error => {
          devError('Failed to save workspace after chart load:', error);
        });
      }

      // Set as active if requested (React 18 batches this with previous updates)
      if (options.makeActive) {
        setActiveChartId(chartId);

        // Emit chart activation event
        try {
          emitChartActivated(chartId, undefined, 'programmatic', 'MultipleChartsContext:loadChart');
        } catch (error) {
          devWarn(`⚠️ [MultipleChartsContext] Failed to emit single chart activation:`, error);
        }
      }

      // Emit chart.loaded event for count determination (no timeout - events are sync)
      try {
        globalEventStream.emit(createEvent<BaseEvent>(
          'chart.loaded',
          'MultipleChartsContext',
          {
            chartId,
            timestamp: Date.now(),
            metadata: {
              totalChartsLoaded: loadedChartIds.length + 1, // +1 because this is being added
              isActive: !!options.makeActive,
              chartName: metadata?.name || 'Unnamed Chart'
            }
          }
        ));
      } catch (error) {
        devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart.loaded event:`, error);
      }

      // Emit chart.data.updated event with snapshotId for initial render (DRY helper)
      try {
        const { snapshotId, apiResponse } = getActiveSnapshotInfo(chartInstance, chartId, chartData.apiResponse);
        globalEventStream.emit(createEvent(
          `chart.data.updated.${chartId}`,
          'MultipleChartsContext:loadChart',
          { chartId, snapshotId, apiResponse }
        ));
      } catch (error) {
        devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart.data.updated event:`, error);
      }

      // Pipeline: Stage 3 - RENDERING (SVG render will happen via chart.data.updated event)
      emitPipelineEvent('chart.pipeline.stage', chartId, { stage: 'rendering' });

      // Pipeline: Complete
      emitPipelineEvent('chart.pipeline.complete', chartId);


    } catch (error) {
      emitPipelineEvent('chart.pipeline.error', chartId, { error: String(error) });
      devError('Failed to load chart:', chartId, error);
      throw error;
    } finally {
      // Remove from loading state
      setLoadingCharts(prev => {
        const newSet = new Set(prev);
        newSet.delete(chartId);
        return newSet;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts, loadedChartIds, workspace, activeChartId, storageService]);

  /**
   * Load multiple charts simultaneously with proper restore logic
   */
  const loadMultipleCharts = useCallback(async (
    chartIds: string[], 
    restoreActiveChart?: string
  ): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Load all charts without setting any as active yet
      const loadPromises = chartIds.map((chartId, index) => 
        loadChart(chartId, { 
          makeActive: false, // Don't set active during parallel loading
          insertAtIndex: index // Use correct sequential index
        })
      );
      
      await Promise.all(loadPromises);
      
      // Set active chart AFTER all charts are loaded
      const newActiveChartId = restoreActiveChart && chartIds.includes(restoreActiveChart)
        ? restoreActiveChart
        : chartIds.length > 0 ? chartIds[0] : null;

      devLog('[MultipleChartsContext] 📊 loadMultipleCharts active chart:', {
        restoreActiveChart,
        chartIds,
        includes: restoreActiveChart ? chartIds.includes(restoreActiveChart) : false,
        newActiveChartId
      });

      if (newActiveChartId) {
        // Set active chart and sync to workspace (React 18 batches with previous updates)
        setActiveChartId(newActiveChartId);
        setWorkspace(prev => ({ ...prev, activeChartId: newActiveChartId }));

        // Emit chart activation event
        try {
          emitChartActivated(newActiveChartId, undefined, 'programmatic', 'MultipleChartsContext:loadMultipleCharts');
          if (debugMode) {
            devLog(`📡 [MultipleChartsContext] Restored active chart: ${newActiveChartId}`);
          }
        } catch (error) {
          devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart activation:`, error);
        }
      }
      
      
    } catch (error) {
      devError('Failed to load multiple charts:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadChart]);

  /**
   * Unload chart from workspace
   */
  const unloadChart = useCallback((chartId: string): void => {
    // Calculate new state
    const newLoadedIds = loadedChartIds.filter(id => id !== chartId);
    const newActiveId = activeChartId === chartId
      ? (newLoadedIds.length > 0 ? newLoadedIds[0] : null)
      : activeChartId;

    // Remove from charts map
    setCharts(prev => {
      const newMap = new Map(prev);
      newMap.delete(chartId);
      return newMap;
    });

    // Remove from loaded IDs
    setLoadedChartIds(newLoadedIds);

    // Handle active chart change
    const isChangingActiveChart = newActiveId && newActiveId !== activeChartId;
    setActiveChartId(newActiveId);

    // If switching to a new active chart, need to load it if not loaded
    if (isChangingActiveChart) {
      const newActiveChart = charts.get(newActiveId);
      if (!newActiveChart) {
        // Lazy load the new active chart
        loadChart(newActiveId, { makeActive: true }).catch(error => {
          devError(`Failed to lazy load chart ${newActiveId} after tab close:`, error);
        });
      } else {
        // Chart already loaded, just emit activation event
        emitChartActivated(newActiveId, chartId, 'programmatic', 'MultipleChartsContext:unloadChart');
      }
    }

    // Update workspace with immediate state and save
    // Remove tabMeta for unloaded chart
    const { [chartId]: _, ...remainingTabMeta } = workspace.tabMeta || {};
    const updatedWorkspace: WorkspaceState = {
      ...workspace,
      charts: newLoadedIds,
      tabOrder: newLoadedIds,
      activeChartId: newActiveId,
      tabMeta: remainingTabMeta,
      modified: new Date()
    };

    setWorkspace(updatedWorkspace);

    // Save workspace state immediately with the updated workspace
    storageService.saveDefaultWorkspace(updatedWorkspace).then(result => {
      if (!result.success) {
        devError('Failed to save workspace after tab close:', result.error);
      } else {
      }
    }).catch(error => {
      devError('Error saving workspace after tab close:', error);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedChartIds, activeChartId, workspace, storageService]);

  /**
   * Switch to specific chart
   * Lazy loading: If chart not loaded, load it first then switch
   */
  const switchToChart = useCallback((chartId: string): void => {
    // Lazy loading check: If chart not loaded, load it first
    if (!charts.has(chartId)) {
      devLog(`[MultipleChartsContext] 🔄 Chart ${chartId} not loaded, triggering lazy load...`);

      // Load the chart and make it active (event chain: loadChart → chart.loaded → chart.data.updated)
      loadChart(chartId, { makeActive: true }).catch(error => {
        devError(`[MultipleChartsContext] Failed to lazy load chart ${chartId}:`, error);
      });
      return;
    }

    const previousChartId = activeChartId;

    // Update active chart state (React 18 batches automatically)
    setActiveChartId(chartId);

    // Emit chart activation events
    try {
      // Deactivate previous chart
      if (previousChartId && previousChartId !== chartId) {
        emitChartDeactivated(previousChartId, 'MultipleChartsContext');

        // Cancel any active planet drag on the previous chart
        globalEventStream.emit(createEvent<BaseEvent>(
          'planet.drag.cancel',
          'MultipleChartsContext:switchToChart',
          { metadata: { chartId: previousChartId, reason: 'tab_switch' } }
        ));
      }

      // Activate new chart
      emitChartActivated(chartId, previousChartId || undefined, 'tab-click', 'MultipleChartsContext');
    } catch (error) {
      devWarn(`⚠️ [MultipleChartsContext] Failed to emit chart navigation events:`, error);
    }

    // Trigger lazy planet rendering (no timeout - sync call)
    try {
      const tabChartRenderers = window.tabChartRenderers;
      if (tabChartRenderers?.has?.(chartId)) {
        const renderer = tabChartRenderers.get(chartId) as any;
        if (renderer?.renderPendingPlanets) {
          renderer.renderPendingPlanets();
        }
      }
    } catch (error) {
      devWarn(`⚠️ [MultipleChartsContext] Failed to trigger lazy planet rendering:`, error);
    }

    // NOTE: lastAccessed update removed - was causing unnecessary re-renders
    // lastAccessed is updated when chart data actually changes (in updateChart)

    // Update workspace
    const updatedWorkspace = {
      ...workspace,
      activeChartId: chartId,
      accessed: new Date()
    };
    setWorkspace(updatedWorkspace);

    // Save workspace with new active chart (skip during restoration)
    if (!isRestoringWorkspaceRef.current) {
      storageService.saveDefaultWorkspace(updatedWorkspace).catch(error => {
        devError('Failed to save workspace after tab switch:', error);
      });
    }

  }, [charts, activeChartId, loadedChartIds, workspace, storageService, loadChart]);

  /**
   * Get active chart instance
   */
  const getActiveChart = useCallback((): ChartInstance | null => {
    return activeChartId ? charts.get(activeChartId) || null : null;
  }, [charts, activeChartId]);

  /**
   * Get specific chart instance
   */
  const getChart = useCallback((chartId: string): ChartInstance | null => {
    return charts.get(chartId) || null;
  }, [charts]);

  /**
   * Get all loaded charts
   */
  const getAllLoadedCharts = useCallback((): ChartInstance[] => {
    return Array.from(charts.values());
  }, [charts]);

  /**
   * Update chart instance
   */
  const updateChart = useCallback((
    chartId: string,
    updates: Partial<ChartInstance>
  ): void => {
    setCharts(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(chartId);

      if (existing) {
        // Check if updates actually change anything
        let hasChanges = false;
        const updatedChart = { ...existing };

        for (const [key, value] of Object.entries(updates)) {
          if (key === 'settings') {
            // Deep compare settings
            const existingSettings = existing.settings || {};
            const newSettings = { ...existingSettings, ...value };

            if (JSON.stringify(existingSettings) !== JSON.stringify(newSettings)) {
              updatedChart.settings = newSettings;
              hasChanges = true;
            }
          } else if ((key === 'apiResponse' || key === 'metadata') && !updates.chartData) {
            // OPTIMIZATION: For metadata-only changes (name/place edit),
            // mutate in place and DON'T trigger Map update.
            // This prevents cascade re-renders.
            if (key === 'apiResponse' && existing.apiResponse !== value) {
              existing.apiResponse = value;
              if (existing.chartData) {
                existing.chartData.apiResponse = value;
              }
            } else if (key === 'metadata' && existing.metadata !== value) {
              existing.metadata = value as typeof existing.metadata;
              // Save to storage
              storageService.updateChartMetadata(chartId, existing.metadata)
                .catch(error => devError(`Failed to save metadata for ${chartId}:`, error));
            }
            // hasChanges stays false - no Map update needed
          } else if (existing[key as keyof ChartInstance] !== value) {
            (updatedChart as any)[key] = value;
            hasChanges = true;
          }
        }

        if (hasChanges) {
          updatedChart.lastAccessed = new Date();
          //     }
          //     return existing[key as keyof ChartInstance] !== (updatedChart as any)[key];
          //   })
          // });

          newMap.set(chartId, updatedChart);

          // Auto-save to storage when metadata is updated
          if (updates.metadata) {
            // Save updated chart to storage to persist metadata changes
            storageService.updateChartMetadata(chartId, updatedChart.metadata)
              .then(result => {
                if (!result.success) {
                  devError(`Failed to save updated metadata for ${chartId}:`, result.error);
                } else {
                }
              })
              .catch(error => {
                devError(`Error saving metadata for ${chartId}:`, error);
              });
          }

          // Auto-save settings (including muhurta chakra state) to storage
          if (updates.settings) {
            setLocalStorageItem(`chart-settings-${chartId}`, updatedChart.settings);
          }

          return newMap;
        } else {
          return prev; // Return unchanged map to prevent unnecessary re-renders
        }
      }

      return prev;
    });
  }, [charts, storageService]);

  /**
   * Update chart data
   */
  const updateChartData = useCallback((
    chartId: string, 
    chartData: ParsedChartData
  ): void => {
    updateChart(chartId, { chartData });
  }, [updateChart]);

  /**
   * Update chart markers
   */
  const updateChartMarkers = useCallback((
    chartId: string, 
    markers: ChartMarker[]
  ): void => {
    updateChart(chartId, { markers });
  }, [updateChart]);

  /**
   * Update chart color preset
   */
  const updateChartPreset = useCallback((
    chartId: string, 
    presetId: string
  ): void => {
    updateChart(chartId, { activePresetId: presetId });
  }, [updateChart]);

  /**
   * Save workspace to storage
   */
  const saveWorkspace = useCallback(async (): Promise<void> => {
    try {
      const result = await storageService.saveDefaultWorkspace(workspace);
      
      if (!result.success) {
        throw new Error(`Failed to save workspace: ${result.error}`);
      }
      
    } catch (error) {
      devError('Failed to save workspace:', error);
    }
  }, [workspace, storageService]);

  /**
   * Load workspace from storage with LAZY LOADING
   * Architecture: Show tabs immediately, load only active chart data
   * Event-driven: emits workspace.restored when complete
   */
  const loadWorkspace = useCallback(async (workspaceId?: string): Promise<void> => {
    // Set flag to prevent saving during restoration
    isRestoringWorkspaceRef.current = true;

    try {
      const result = await storageService.getDefaultWorkspace();

      if (result.success && result.data) {
        // Use localStorage as fallback for activeChartId (sync storage survives fast reload)
        const localActiveChartId = getLocalStorageItem<string | null>('hora_activeChartId', null);
        const finalActiveChartId = localActiveChartId && result.data.charts.includes(localActiveChartId)
          ? localActiveChartId
          : result.data.activeChartId;

        devLog('[MultipleChartsContext] 📂 Loading workspace (lazy):', {
          charts: result.data.charts,
          storedActiveChartId: result.data.activeChartId,
          localActiveChartId,
          finalActiveChartId,
          tabMeta: result.data.tabMeta
        });

        // LAZY LOADING: Set workspace state and loadedChartIds for tab display
        // Tabs appear immediately with names from tabMeta
        // Sync workspace.activeChartId with finalActiveChartId (may differ from IndexedDB value
        // when localStorage had a more recent value)
        const workspaceWithTabMeta: WorkspaceState = {
          ...result.data,
          activeChartId: finalActiveChartId,
          tabMeta: result.data.tabMeta || {} // Ensure tabMeta exists
        };

        flushSync(() => {
          setWorkspace(workspaceWithTabMeta);
          setLoadedChartIds(result.data.charts); // Show all tabs immediately
          setActiveChartId(finalActiveChartId);
        });

        // LAZY LOADING: Only load the active chart's data
        // Other charts will be loaded on demand when user switches tabs
        if (finalActiveChartId) {
          try {
            await loadChart(finalActiveChartId, { makeActive: true });
            devLog(`[MultipleChartsContext] ✅ Active chart loaded: ${finalActiveChartId}`);
          } catch (error) {
            devError(`[MultipleChartsContext] Failed to load active chart ${finalActiveChartId}:`, error);
          }
        }

        // Emit workspace.restored event
        globalEventStream.emit(createEvent(
          'workspace.restored',
          'MultipleChartsContext:loadWorkspace',
          {
            chartCount: result.data.charts.length,
            activeChartId: finalActiveChartId,
            lazyLoading: true
          }
        ));
      } else {
        // No workspace found - emit with empty state
        globalEventStream.emit(createEvent(
          'workspace.restored',
          'MultipleChartsContext:loadWorkspace',
          { chartCount: 0, activeChartId: null }
        ));
      }
    } catch (error) {
      devError('Failed to load workspace:', error);
      // Emit even on error to ensure flag is cleared
      globalEventStream.emit(createEvent(
        'workspace.restored',
        'MultipleChartsContext:loadWorkspace',
        { chartCount: 0, activeChartId: null, error: true }
      ));
    } finally {
      // Always reset flag synchronously at the end of loadWorkspace
      // (don't rely on event subscription timing)
      isRestoringWorkspaceRef.current = false;
    }
  }, [storageService, loadChart, debugMode]);

  /**
   * Close chart tab
   */
  const closeTab = useCallback((chartId: string): void => {
    unloadChart(chartId);
  }, [unloadChart]);

  /**
   * Reorder tabs by moving chart from one position to another
   */
  const reorderTabs = useCallback((fromIndex: number, toIndex: number): void => {
    // Normalize and validate indices
    const maxIndex = loadedChartIds.length - 1;
    const normalizedFromIndex = Math.max(0, Math.min(fromIndex, maxIndex));
    const normalizedToIndex = Math.max(0, Math.min(toIndex, maxIndex));
    
    if (normalizedFromIndex === normalizedToIndex || loadedChartIds.length <= 1) {
      return; // No change needed
    }
    
    // Validate that we have valid chart IDs at these indices
    if (!loadedChartIds[normalizedFromIndex]) {
      devWarn(`No chart found at normalized index ${normalizedFromIndex} (original: ${fromIndex})`);
      return;
    }

    const newLoadedIds = [...loadedChartIds];
    
    // Remove the item from the source position using normalized index
    const [movedChartId] = newLoadedIds.splice(normalizedFromIndex, 1);
    
    // Ensure we have a valid chart ID
    if (!movedChartId) {
      devError(`No chart found at normalized index ${normalizedFromIndex}`);
      return;
    }
    
    // Insert it at the target position using normalized index
    newLoadedIds.splice(normalizedToIndex, 0, movedChartId);
    
    // Update state
    setLoadedChartIds(newLoadedIds);

    // Update workspace with immediate state and save
    const updatedWorkspace: WorkspaceState = {
      ...workspace,
      charts: newLoadedIds,
      tabOrder: newLoadedIds,
      modified: new Date()
    };

    setWorkspace(updatedWorkspace);

    // Save workspace state immediately with the updated workspace
    storageService.saveDefaultWorkspace(updatedWorkspace).then(result => {
      if (!result.success) {
        devError('Failed to save workspace after tab reorder:', result.error);
      }
    }).catch(error => {
      devError('Error saving workspace after tab reorder:', error);
    });
    
  }, [loadedChartIds, workspace, storageService]);

  /**
   * Duplicate chart in new tab
   */
  const duplicateChart = useCallback(async (sourceChartId: string): Promise<string> => {
    const sourceChart = charts.get(sourceChartId);
    
    if (!sourceChart) {
      throw new Error(`Source chart not found: ${sourceChartId}`);
    }

    // Generate new ID for duplicate (with timestamp suffix to avoid collision)
    const timestamp = Date.now();
    const duplicateId = `${sourceChartId}_dup_${timestamp}`;

    // Create duplicate instance
    const duplicateChart: ChartInstance = {
      ...sourceChart,
      id: duplicateId,
      lastAccessed: new Date()
    };

    // Add to charts
    setCharts(prev => new Map([...prev, [duplicateId, duplicateChart]]));

    // Add to loaded charts
    const insertIndex = loadedChartIds.indexOf(sourceChartId) + 1;
    const newLoadedIds = [...loadedChartIds];
    newLoadedIds.splice(insertIndex, 0, duplicateId);
    
    setLoadedChartIds(newLoadedIds);

    // Update workspace
    setWorkspace(prev => ({
      ...prev,
      charts: newLoadedIds,
      tabOrder: newLoadedIds,
      modified: new Date()
    }));

    await saveWorkspace();

    return duplicateId;
  }, [charts, loadedChartIds, saveWorkspace]);

  /**
   * Recalculate chart for current time and add as snapshot
   * Replaces createNewChartWithCurrentTime - adds snapshot instead of new chart
   */
  const addCurrentTimeSnapshot = useCallback(async (chartId: string): Promise<void> => {
    const chart = charts.get(chartId);

    if (!chart) {
      throw new Error(`Chart not found: ${chartId}`);
    }

    // Extract coordinates from API response
    const basicInfo = chart.apiResponse?.basic_info;
    const latitude = basicInfo?.latitude || basicInfo?.latitudeValue;
    const longitude = basicInfo?.longitude || basicInfo?.longitudeValue;

    if (!latitude || !longitude) {
      throw new Error('Chart missing coordinate data');
    }

    // Get current time
    const now = new Date();
    const date = now.toLocaleDateString('en-GB').replace(/\//g, '-'); // DD-MM-YYYY
    const time = now.toLocaleTimeString('en-GB', { hour12: false }); // HH:MM:SS

    // Get browser timezone offset
    const offset = now.getTimezoneOffset();
    const offsetHours = -offset / 60;
    const offsetSign = offsetHours >= 0 ? '+' : '-';
    const timezoneOffset = `${offsetSign}${String(Math.abs(Math.floor(offsetHours))).padStart(2, '0')}:${String(Math.abs((offsetHours % 1) * 60)).padStart(2, '0')}`;

    const birthData = {
      chartName: chart.metadata?.name || 'Chart',
      locationName: basicInfo?.place_name || basicInfo?.location || 'Unknown',
      date,
      time,
      longitude: Number(longitude),
      latitude: Number(latitude),
      timezoneMode: 'auto' as const,
      timezoneOffset,
      selectedLocation: basicInfo?.place_name || 'Unknown'
    };

    // Use API transformation utilities
    const { birthDataToJatakaRequest, jatakaResponseToChartData } = await import('../../../utils/apiDataTransformers');
    const { jatakaApiService } = await import('../../../services/JatakaApiService');

    // Make API request
    const jatakaRequest = birthDataToJatakaRequest(birthData);
    const apiResult = await jatakaApiService.calculateJataka(jatakaRequest);

    if (!apiResult.success || !apiResult.data) {
      throw new Error(`API request failed: ${apiResult.error?.message || 'Unknown error'}`);
    }

    // Transform response to chart data
    const newChartData = jatakaResponseToChartData(apiResult.data, birthData);

    // Create snapshot - use datetime_iso for precision if available
    const newBasicInfo = newChartData.apiResponse?.basic_info;
    const snapshot: ChartStateSnapshot = {
      id: generateSnapshotId(),
      apiResponse: newChartData.apiResponse,
      chartData: newChartData,
      createdAt: new Date(),
      chartDateTime: newBasicInfo?.datetime_iso || `${date} ${time}`,
      source: 'new-time',
      label: time
    };

    // Cancel any active planet drag before creating new snapshot
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.cancel',
      'MultipleChartsContext:addCurrentTimeSnapshot',
      { metadata: { chartId, reason: 'new_snapshot' } }
    ));

    // Calculate new history (for saving to storage)
    const newHistory = addSnapshotToHistory(chart.history || [], snapshot);

    // Update chart + add snapshot in single operation (sync for immediate event emit)
    flushSync(() => {
      setCharts(prev => {
        const existing = prev.get(chartId);
        if (!existing) return prev;

        const newMap = new Map(prev);
        newMap.set(chartId, {
          ...existing,
          chartData: newChartData,
          apiResponse: newChartData.apiResponse,
          history: newHistory,
          currentHistoryIndex: CURRENT_LIVE_INDEX
        });

        devLog(`[MultipleChartsContext] 📸 Current time snapshot added for ${chartId}, history: ${newHistory.length}`);
        return newMap;
      });
    });

    // Auto-save history to storage
    storageService.updateChartHistory(chartId, newHistory).catch(error => {
      devError(`[MultipleChartsContext] Failed to save history for ${chartId}:`, error);
    });

    // Clear chakra cache before re-render
    globalEventStream.emit(createEvent(
      `chart.settings.changed.${chartId}`,
      'MultipleChartsContext:addCurrentTimeSnapshot',
      { chartId }
    ));

    // Emit update event for chart re-render (with apiResponse for chakras)
    globalEventStream.emit(createEvent(
      `chart.data.updated.${chartId}`,
      'MultipleChartsContext:addCurrentTimeSnapshot',
      {
        chartId,
        snapshotId: snapshot.id,
        apiResponse: newChartData.apiResponse
      }
    ));
  }, [charts, storageService]);

  /**
   * Load workspace on storage ready (event-driven, no timeouts)
   * Replaces auto-load of single chart with full workspace restoration
   */
  useEffect(() => {
    let mounted = true;
    let workspaceLoadInProgress = false;
    let workspaceLoadCompleted = false;

    const handleStorageReady = async () => {
      if (workspaceLoadInProgress || workspaceLoadCompleted) {
        return;
      }

      // Skip if charts already loaded
      if (loadedChartIds.length > 0) {
        workspaceLoadCompleted = true;
        return;
      }

      workspaceLoadInProgress = true;
      if (debugMode) devLog('🔄 [MultipleChartsContext] Storage ready, loading workspace...');

      try {
        if (mounted) {
          await loadWorkspace();
          workspaceLoadCompleted = true;
          if (debugMode) devLog('🔄 [MultipleChartsContext] Workspace loaded successfully');
        }
      } catch (error) {
        devError('🔄 [MultipleChartsContext] Failed to load workspace:', error);
      } finally {
        workspaceLoadInProgress = false;
      }
    };

    // Listen for storage ready event
    const storageReadySubscription = globalEventStream.subscribe('storage.ready', handleStorageReady);

    // Listen for workspace.restored to clear the restoring flag
    const workspaceRestoredSubscription = globalEventStream.subscribe('workspace.restored', () => {
      isRestoringWorkspaceRef.current = false;
      if (debugMode) devLog('🔄 [MultipleChartsContext] Workspace restored, saving enabled');
    });

    return () => {
      mounted = false;
      storageReadySubscription.unsubscribe();
      workspaceRestoredSubscription.unsubscribe();
    };
  }, [loadWorkspace, loadedChartIds.length, debugMode]);

  /**
   * Sync activeChartId to localStorage for instant restore on reload
   * IndexedDB is async and may not complete before page unload
   * Note: Always save to localStorage (sync) regardless of isRestoring flag
   */
  useEffect(() => {
    if (activeChartId) {
      setLocalStorageItem('hora_activeChartId', activeChartId);
    }
  }, [activeChartId]);

  /**
   * Sync loadedChartIds to localStorage for instant restore on reload
   */
  useEffect(() => {
    if (loadedChartIds.length > 0) {
      setLocalStorageItem('hora_loadedChartIds', loadedChartIds);
    }
  }, [loadedChartIds]);


  // =========================================================================
  // History Management Methods
  // =========================================================================

  /**
   * Add a new snapshot to chart history
   * Maintains max MAX_CHART_HISTORY snapshots, removes oldest when exceeded
   */
  const addChartSnapshot = useCallback((chartId: string, snapshot: Omit<ChartStateSnapshot, 'id'>) => {
    const chart = charts.get(chartId);
    if (!chart) return;

    const newSnapshot: ChartStateSnapshot = {
      ...snapshot,
      id: generateSnapshotId()
    };

    // Add new snapshot, maintain max history size
    const newHistory = addSnapshotToHistory(chart.history, newSnapshot);

    setCharts(prev => {
      const existing = prev.get(chartId);
      if (!existing) return prev;

      const newMap = new Map(prev);
      newMap.set(chartId, {
        ...existing,
        history: newHistory,
        currentHistoryIndex: CURRENT_LIVE_INDEX  // Reset to current/live state after adding
      });

      return newMap;
    });

    // Auto-save history to storage
    storageService.updateChartHistory(chartId, newHistory).catch(error => {
      devError(`[MultipleChartsContext] Failed to save history for ${chartId}:`, error);
    });

    devLog(`[MultipleChartsContext] 📸 Snapshot added for ${chartId}, history size: ${newHistory.length}`);
  }, [charts, storageService]);

  /**
   * Navigate to a specific history index
   * -1 = current/live state, 0+ = history index
   */
  const navigateHistory = useCallback((chartId: string, index: number) => {
    const chart = charts.get(chartId);
    if (!chart) return;

    // Skip if already at this index (prevent redundant navigation)
    // Handle case where currentHistoryIndex is -1 (current/live) which means last item in history
    const effectiveCurrentIndex = chart.currentHistoryIndex === CURRENT_LIVE_INDEX
      ? chart.history.length - 1
      : chart.currentHistoryIndex;

    if (effectiveCurrentIndex === index) {
      devLog(`[MultipleChartsContext] Already at history index ${index} (effective: ${effectiveCurrentIndex}), skipping navigation`);
      return;
    }

    // Validate index
    if (index < -1 || index >= chart.history.length) {
      devWarn(`[MultipleChartsContext] Invalid history index ${index} for chart ${chartId}`);
      return;
    }

    // Get target snapshot data
    const targetSnapshot = index === -1
      ? chart.history[chart.history.length - 1]
      : chart.history[index];

    if (!targetSnapshot) {
      devWarn(`[MultipleChartsContext] No snapshot found at index ${index}`);
      return;
    }

    // Cancel any active planet drag before changing snapshot
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.cancel',
      'MultipleChartsContext:navigateHistory',
      { metadata: { chartId, reason: 'snapshot_switch' } }
    ));

    // Use flushSync to ensure React state is updated synchronously before emitting events
    // ChakraDataService reads from context, needs fresh data
    flushSync(() => {
      setCharts(prev => {
        const existing = prev.get(chartId);
        if (!existing) return prev;

        const newMap = new Map(prev);
        newMap.set(chartId, {
          ...existing,
          chartData: targetSnapshot.chartData,
          apiResponse: targetSnapshot.apiResponse,
          currentHistoryIndex: index
        });

        return newMap;
      });
    });

    // Save currentHistoryIndex to storage
    storageService.updateChartHistory(chartId, chart.history, index).catch(error => {
      devError(`[MultipleChartsContext] Failed to save history index:`, error);
    });

    // Clear state cache to force full re-render (chakras need fresh data)
    globalEventStream.emit(createEvent(
      `chart.settings.changed.${chartId}`,
      'MultipleChartsContext:navigateHistory',
      { chartId }
    ));

    // Emit event to trigger chart re-render
    globalEventStream.emit(createEvent(
      `chart.data.updated.${chartId}`,
      'MultipleChartsContext:navigateHistory',
      {
        chartId,
        snapshotId: targetSnapshot.id,
        apiResponse: targetSnapshot.apiResponse
      }
    ));

    devLog(`[MultipleChartsContext] 🕐 Navigated to history index ${index} for ${chartId}`);
  }, [charts, storageService]);

  /**
   * Get history for a chart
   */
  const getChartHistory = useCallback((chartId: string): ChartStateSnapshot[] => {
    const chart = charts.get(chartId);
    return chart?.history || [];
  }, [charts]);

  /**
   * Clear all history for a chart
   */
  const clearChartHistory = useCallback((chartId: string) => {
    setCharts(prev => {
      const chart = prev.get(chartId);
      if (!chart) return prev;

      const newMap = new Map(prev);
      newMap.set(chartId, {
        ...chart,
        history: [],
        currentHistoryIndex: CURRENT_LIVE_INDEX
      });

      devLog(`[MultipleChartsContext] 🗑️ History cleared for ${chartId}`);
      return newMap;
    });
  }, []);

  /**
   * Delete a specific snapshot from history
   * Cannot delete original (index 0) or currently active snapshot
   */
  const deleteChartSnapshot = useCallback((chartId: string, snapshotIndex: number) => {
    const chart = charts.get(chartId);
    if (!chart) return;

    // Cannot delete original snapshot (index 0)
    if (snapshotIndex === 0) {
      devWarn(`[MultipleChartsContext] Cannot delete original snapshot`);
      return;
    }

    // Cannot delete if it's the active snapshot
    const isActive = snapshotIndex === chart.currentHistoryIndex ||
      (chart.currentHistoryIndex === CURRENT_LIVE_INDEX && snapshotIndex === chart.history.length - 1);
    if (isActive) {
      devWarn(`[MultipleChartsContext] Cannot delete active snapshot`);
      return;
    }

    // Calculate new history
    const newHistory = chart.history.filter((_, idx) => idx !== snapshotIndex);

    // Adjust currentHistoryIndex if needed
    let newIndex = chart.currentHistoryIndex;
    if (chart.currentHistoryIndex > snapshotIndex) {
      newIndex = chart.currentHistoryIndex - 1;
    }

    setCharts(prev => {
      const existing = prev.get(chartId);
      if (!existing) return prev;

      const newMap = new Map(prev);
      newMap.set(chartId, {
        ...existing,
        history: newHistory,
        currentHistoryIndex: newIndex
      });

      return newMap;
    });

    // Save to storage
    storageService.updateChartHistory(chartId, newHistory).catch(error => {
      devError(`[MultipleChartsContext] Failed to save history after delete:`, error);
    });

    devLog(`[MultipleChartsContext] 🗑️ Snapshot ${snapshotIndex} deleted for ${chartId}`);
  }, [charts, storageService]);

  /**
   * Undo - go to previous state in history
   */
  const undoChartState = useCallback((chartId: string) => {
    const chart = charts.get(chartId);
    if (!chart || chart.history.length === 0) return;

    const currentIndex = chart.currentHistoryIndex;
    // If at current (-1), go to last history item
    // If at history item, go to previous
    const newIndex = currentIndex === -1
      ? chart.history.length - 2  // Go to second-to-last (last is current state)
      : currentIndex - 1;

    if (newIndex >= 0) {
      navigateHistory(chartId, newIndex);
    }
  }, [charts, navigateHistory]);

  /**
   * Redo - go to next state in history
   */
  const redoChartState = useCallback((chartId: string) => {
    const chart = charts.get(chartId);
    if (!chart || chart.history.length === 0) return;

    const currentIndex = chart.currentHistoryIndex;
    if (currentIndex === -1) return;  // Already at current state

    const newIndex = currentIndex + 1;
    if (newIndex >= chart.history.length - 1) {
      // Go back to current/live state
      navigateHistory(chartId, -1);
    } else {
      navigateHistory(chartId, newIndex);
    }
  }, [charts, navigateHistory]);

  /**
   * Synchronize workspace with loadedChartIds and activeChartId changes
   * This is the authoritative sync: React state → workspace.activeChartId
   * Individual operations also set workspace.activeChartId inline for immediate saves,
   * but this effect acts as a safety net to ensure consistency.
   */
  useEffect(() => {
    setWorkspace(prev => {
      const needsChartsUpdate = prev.charts !== loadedChartIds || prev.tabOrder !== loadedChartIds;
      const needsActiveUpdate = prev.activeChartId !== activeChartId;

      if (!needsChartsUpdate && !needsActiveUpdate) return prev;

      return {
        ...prev,
        charts: loadedChartIds,
        tabOrder: loadedChartIds,
        activeChartId
      };
    });
  }, [loadedChartIds, activeChartId]);

  // Note: Workspace initialization moved to storage.ready event handler above
  // This ensures storage is ready before attempting to load charts

  /**
   * Configure and start PlanetDragIntegrationService
   */
  useEffect(() => {
    // Chart data provider - returns location info for a chart
    const chartDataProvider = (chartId: string) => {
      const chart = charts.get(chartId);
      if (!chart) return null;

      const basicInfo = chart.chartData?.apiResponse?.basic_info;
      if (!basicInfo) return null;

      // Parse timezone offset from string like "+05:30" or "-08:00"
      let timezoneOffset = 0;
      if (basicInfo.timezone) {
        const match = basicInfo.timezone.match(/([+-]?)(\d{1,2}):?(\d{2})?/);
        if (match) {
          const sign = match[1] === '-' ? -1 : 1;
          const hours = parseInt(match[2], 10);
          const minutes = parseInt(match[3] || '0', 10);
          timezoneOffset = sign * (hours + minutes / 60);
        }
      }

      return {
        location: {
          latitude: basicInfo.latitudeValue ?? 0,
          longitude: basicInfo.longitudeValue ?? 0,
          timezoneOffset,
          placeName: getPlaceName(basicInfo)
        },
        chartName: chart.metadata?.name || basicInfo.chart_name || 'Chart'
      };
    };

    // Chart data updater - updates chart after recalculation (degree-move or time-move)
    const chartDataUpdater = (chartId: string, chartData: ParsedChartData, source: SnapshotSource) => {
      // Get existing chart for history
      const existing = charts.get(chartId);
      if (!existing) {
        devWarn(`[MultipleChartsContext] Chart ${chartId} not found for motion update`);
        return;
      }

      // Create snapshot for history
      const basicInfo = chartData.apiResponse?.basic_info;
      // Use datetime_iso for precise time comparison (includes microseconds)
      const chartDateTime = basicInfo?.datetime_iso
        || (basicInfo ? `${basicInfo.date} ${basicInfo.time}` : new Date().toISOString());

      const snapshot: ChartStateSnapshot = {
        id: generateSnapshotId(),
        apiResponse: chartData.apiResponse,
        chartData: chartData,
        createdAt: new Date(),
        chartDateTime,
        source,
        label: basicInfo?.time || 'Unknown time'
      };

      // Add snapshot to history, maintain max size
      const newHistory = addSnapshotToHistory(existing.history, snapshot);

      // Update chart state (sync for immediate event emit)
      flushSync(() => {
        setCharts(prev => {
          const current = prev.get(chartId);
          if (!current) return prev;

          const newMap = new Map(prev);
          newMap.set(chartId, {
            ...current,
            chartData,
            apiResponse: chartData.apiResponse,
            lastAccessed: new Date(),
            history: newHistory,
            currentHistoryIndex: CURRENT_LIVE_INDEX  // Always at current/live after update
          });

          return newMap;
        });
      });

      devLog(`[MultipleChartsContext] 📸 Planet drag snapshot saved for ${chartId}, history: ${newHistory.length}`);

      // Side effects (after state update)
      // 1. Save history to IndexedDB
      storageService.updateChartHistory(chartId, newHistory).catch(error => {
        devError(`[MultipleChartsContext] Failed to save history after planet drag:`, error);
      });

      // 2. Clear chakra cache before re-render
      globalEventStream.emit(createEvent(
        `chart.settings.changed.${chartId}`,
        'MultipleChartsContext:planetDrag',
        { chartId }
      ));

      // 3. Emit event to trigger full chart re-render (including chakras)
      globalEventStream.emit(createEvent(
        `chart.data.updated.${chartId}`,
        'MultipleChartsContext:planetDrag',
        {
          chartId,
          snapshotId: snapshot.id,
          apiResponse: chartData.apiResponse
        }
      ));
    };

    // Configure and start the service
    planetDragIntegrationService.configure(chartDataProvider, chartDataUpdater);
    planetDragIntegrationService.start();

    return () => {
      planetDragIntegrationService.stop();
    };
  }, [charts]); // Re-configure when charts change

  // Context value
  const contextValue: MultipleChartsContextType = useMemo(() => ({
    // State
    charts,
    activeChartId,
    loadedChartIds,
    isLoading,
    loadingCharts,
    workspace,

    // Chart operations
    loadChart,
    loadChartFromData,  // New method for share mode
    loadMultipleCharts,
    unloadChart,
    switchToChart,

    // Data access
    getActiveChart,
    getChart,
    getAllLoadedCharts,

    // Updates
    updateChart,
    updateChartData,
    updateChartMarkers,
    updateChartPreset,

    // Workspace
    saveWorkspace,
    loadWorkspace,

    // Tab management
    reorderTabs,
    closeTab,
    duplicateChart,
    addCurrentTimeSnapshot,

    // History management
    addChartSnapshot,
    navigateHistory,
    getChartHistory,
    clearChartHistory,
    deleteChartSnapshot,
    undoChartState,
    redoChartState
  }), [charts, activeChartId, loadedChartIds, isLoading, loadingCharts, workspace, loadChart, loadChartFromData, loadMultipleCharts, unloadChart, switchToChart, getActiveChart, getChart, getAllLoadedCharts, updateChart, updateChartData, updateChartMarkers, updateChartPreset, saveWorkspace, loadWorkspace, reorderTabs, closeTab, duplicateChart, addCurrentTimeSnapshot, addChartSnapshot, navigateHistory, getChartHistory, clearChartHistory, deleteChartSnapshot, undoChartState, redoChartState]);

  // Make context globally available for chart renderers
  useEffect(() => {
    window.multipleChartsContext = contextValue;
    return () => {
      window.multipleChartsContext = null;
    };
  }, [contextValue]);

  return (
    <MultipleChartsContext.Provider value={contextValue}>
      {children}
    </MultipleChartsContext.Provider>
  );
};

/**
 * Hook to use Multiple Charts Context
 * Replaces useAstrologyChart hook from single chart system
 */
export const useMultipleCharts = (): MultipleChartsContextType => {
  const context = useContext(MultipleChartsContext);
  
  if (!context) {
    throw new Error('useMultipleCharts must be used within MultipleChartsProvider');
  }
  
  return context;
};

/**
 * Hook to get active chart (compatibility with existing components)
 * Provides similar interface to previous useAstrologyChart
 */
export const useActiveChart = () => {
  const { getActiveChart, activeChartId, isLoading } = useMultipleCharts();
  
  const activeChart = getActiveChart();
  
  return {
    chartData: activeChart?.chartData || null,
    isLoading: isLoading || activeChart?.isLoading || false,
    markers: activeChart?.markers || [],
    activePresetId: activeChart?.activePresetId,
    apiResponse: activeChart?.apiResponse,
    
    // Chart identification
    chartId: activeChartId,
    chart: activeChart
  };
};